import { yupResolver } from '@hookform/resolvers/yup';
import {
  ProductAdjustmentModel,
  ProductAdjustmentMutationInput,
} from 'api-hooks/product/adjustment/model';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { FormContent, SeparatorVertical } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { ProductAdjustmentFormSchema } from './form-type';
import InformationField from './information-field';
import ProductVariantField from './product-variant-field';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  productAdjustment?: ProductAdjustmentModel;
  onSubmit: (
    input: ProductAdjustmentMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ProductAdjustmentModel | undefined>;
  renderError?: React.ReactNode;
}

export default function ProductAdjustmentForm(props: Props) {
  const { productAdjustment } = props;
  const { t } = useTranslation();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues(productAdjustment);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(ProductAdjustmentFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        const result = {
          ...values,
          items: values.items.map((item) => ({
            ...item,
            productVariantId: item.productVariantId.id,
            price: item.price || 0,
          })),
        };

        await props.onSubmit(result, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );
  const permission = useGetCrudPermission('product-adjustments');

  const { onDelete, isLoading: deleteLoading } = useDestroyAction(
    'product-adjustments',
    productAdjustment?.id,
  );

  const customViewName =
    productAdjustment &&
    `${t('common:product_adjustments')} - ${productAdjustment.number}`;

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!productAdjustment}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => {
            return (
              <>
                <FormHeader
                  data={productAdjustment}
                  onDelete={onDelete}
                  noDelete={!permission.delete && !editable}
                  title={
                    productAdjustment
                      ? customViewName ?? t('common:product_adjustments')
                      : t('common:add_extra', {
                          extra: t('common:product_adjustments'),
                        })
                  }
                  isDeleteLoading={deleteLoading}
                  noEdit={!permission.update}
                />
              </>
            );
          }}
        </FormState>
        {props.renderError}
        <div>
          <InformationField />
          <SeparatorVertical $margin={24} />
          <ProductVariantField />
          <SeparatorVertical $margin={24} />
        </div>
      </FormContent>
    </Form>
  );
}
